import React, { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { API_BASE_URL } from "../utils/api";
import "../styles/NotificationsPage.css";
import "../styles/notifications-mobile-style.css";

/* ---------- Small helpers ---------- */
const fmtTimeLabel = (iso) => {
  if (!iso) return "";

  try {
    const d = new Date(iso);
    if (isNaN(d)) throw new Error("Invalid Date");

    const now = new Date();

    const sameDay =
      d.getFullYear() === now.getFullYear() &&
      d.getMonth() === now.getMonth() &&
      d.getDate() === now.getDate();

    if (sameDay) {
      return d.toLocaleTimeString("ar-SA", { hour: "2-digit", minute: "2-digit", hour12: false });
    }

    const time = d.toLocaleTimeString("ar-SA", { hour: "2-digit", minute: "2-digit", hour12: false });
    const dayMonth = d.toLocaleDateString("ar-SA", { day: "numeric", month: "long" });
    return `${time} ${dayMonth}`;
  } catch (err) {
    console.warn("Invalid date value:", iso, err);
    return "";
  }
};


const mapServerNotif = (row) => ({
  id: row.id,
  title: row.title ?? "",
  body: row.body ?? "",
  kind: row.kind ?? "info", // 'success' | 'info' | 'deal'
  isRead: !!(row.is_read ?? row.isRead),
  timeLabel: fmtTimeLabel(row.created_at ?? row.timeISO),
});

/* ---------- Icons ---------- */
function KindIcon({ kind }) {
  if (kind === "success") {
    return (
      <span className="notif-icon success" aria-hidden>
        <svg viewBox="0 0 24 24">
          <path d="M20 6L9 17l-5-5" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </span>
    );
  }
  if (kind === "deal") {
    return (
      <span className="notif-icon success" aria-hidden>
        <svg viewBox="0 0 24 24">
          <path
            d="M19 12a7 7 0 11-14 0 7 7 0 0114 0zM8 16l8-8M9 9h.01M15 15h.01"
            fill="none"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
    );
  }
  return (
    <span className="notif-icon success" aria-hidden>
      <svg viewBox="0 0 24 24">
        <path
          d="M6 2h12a2 2 0 012 2v16l-8-5-8 5V4a2 2 0 012-2z"
          fill="none"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </span>
  );
}

/* ---------- Row ---------- */
function NotificationRow({ item, onToggleRead }) {
  return (
    <div
      className={`notif-row ${item.isRead ? "read" : "unread"}`}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") onToggleRead(item.id);
      }}
    >
      <KindIcon kind={item.kind} />
      <div className="notif-card">
        <div className="notif-content">
          <div className="notif-text">
            <div className="notif-title">{item.title}</div>
            {item.body ? <div className="notif-body">{item.body}</div> : null}
          </div>
        </div>
        <div className="notif-actions">
          <div className="notif-time">{item.timeLabel}</div>
        </div>
      </div>
    </div>
  );
}

/* ---------- Page ---------- */
export default function NotificationsPage({ setCurrentView }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const token = typeof window !== "undefined" ? localStorage.getItem("token") : null;
  const authHeader = token ? { Authorization: `Bearer ${token}` } : {};

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/notifications`, { headers: authHeader });
      // Your response shape: { status, data: { items: [...], meta: {...} } }
      const raw = res?.data?.data?.items ?? [];
      const mapped = raw.map(mapServerNotif);
      setItems(mapped);
    } catch (e) {
      console.error("Failed to load notifications:", e);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [token]); // API_BASE_URL is a constant import

  const markAllAsReadRemote = useCallback(async () => {
    try {
      // You said: POST /notifications/mark-all-read
      await axios.post(`${API_BASE_URL}/notifications/mark-all-read`, {}, { headers: authHeader });
      // Optimistically reflect read state
      setItems((prev) => prev.map((n) => ({ ...n, isRead: true })));
    } catch (e) {
      console.warn("mark-all-read failed (ignoring):", e?.response?.data || e.message);
    }
  }, [token]);

  // On mount: fetch then mark all as read
  useEffect(() => {
    (async () => {
      await fetchNotifications();
      await markAllAsReadRemote();
    })();
  }, [fetchNotifications, markAllAsReadRemote]);

  // Local toggle (UI only). If you add a per-item endpoint later, call it here.
  const toggleRead = (id) => {
    setItems((prev) => prev.map((n) => (n.id === id ? { ...n, isRead: !n.isRead } : n)));
  };

  const unreadCount = useMemo(() => items.filter((n) => !n.isRead).length, [items]);

  return (
    <main className="notifications-page" dir="rtl">
      <div className="clip-bg" />
      <div className="container">
        <h1 className="notif-heading">الإشعارات</h1>
        {!token && (
          <div className="fav-state warn">
            <a
              href=""
              onClick={(e) => { e.preventDefault(); setCurrentView?.("login"); }}
              style={{ color: "#CAEA3E", textDecoration: "underline", cursor: "pointer" }}
            >
              سجل دخول عشان توصلك الاشعارات
            </a>

          </div>
        )}
        {loading ? (
          <div className="notif-empty">جارٍ تحميل الإشعارات…</div>
        ) : items.length === 0 ? (
          <div className="notif-empty">لا توجد إشعارات حالياً.</div>
        ) : (
          <div className="notif-list">
            {items.map((n) => (
              <NotificationRow key={n.id} item={n} onToggleRead={toggleRead} />
            ))}
          </div>
        )}
      </div>
    </main>
  );
}
